import { useLingui } from '@lingui/react/macro';
import type { ComponentProps } from 'react';

import { decimalToCents, formatCents, type Cents } from '@/shared/format/money';

import { cn } from './cn';
import { fieldControlClass } from './input';
import { moneyModel, parsePastedMoney } from './internal/money-mask';
import { useMaskedInput } from './internal/use-masked-input';

export type MoneyFieldProps = Omit<ComponentProps<'input'>, 'value' | 'defaultValue' | 'onChange' | 'type'> & {
  /** Centavos inteiros (o que o legado grava). `null` = vazio. */
  readonly value: Cents | null | undefined;
  readonly onChange?: (cents: Cents | null) => void;
  readonly allowNegative?: boolean;
};

function centsToDigits(cents: Cents | null): string {
  if (cents === null || !Number.isSafeInteger(cents)) return '';
  return String(cents);
}

/**
 * Dinheiro em BRL: os dígitos entram pela direita (`1`, `2`, `3` → `R$ 1,23`), exibição no locale
 * ativo, valor em centavos inteiros. Digitar `1.234,56` dá `123456`.
 */
export function MoneyField({ value, onChange, onBlur, allowNegative = false, className, placeholder, ...props }: MoneyFieldProps) {
  const { i18n } = useLingui();
  const model = moneyModel(i18n.locale, allowNegative);
  const bindings = useMaskedInput<Cents | null>({
    model,
    value: value ?? null,
    onChange,
    onBlur,
    toValue: (significant) => {
      const digits = significant.replace('-', '');
      return digits === '' ? null : Number(significant);
    },
    fromValue: (cents) => model.significant(centsToDigits(cents)),
    sameValue: (left, right) => left === right,
    onPasteText: (pasted) => {
      const decimal = parsePastedMoney(pasted);
      return decimal === null ? null : model.significant(String(decimalToCents(decimal)));
    },
  });
  return (
    <input
      type="text"
      inputMode={allowNegative ? 'text' : 'numeric'}
      autoComplete="off"
      spellCheck={false}
      placeholder={placeholder ?? formatCents(0, { locale: i18n.locale })}
      data-slot="money-field"
      className={cn(fieldControlClass, 'text-right tabular-nums', className)}
      {...props}
      {...bindings}
    />
  );
}
